import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import type { SocialPost } from '../components/social/socialTypes';
import * as socialApi from '@/api/social';
import { getApiErrorMessage } from '@/api/axios';

export type FeedScope = 'all' | 'following' | 'myPets';

interface SocialFeedContextValue {
  posts: SocialPost[];
  setPosts: React.Dispatch<React.SetStateAction<SocialPost[]>>;
  isLoading: boolean;
  error: string | null;
  refetch: (scope?: FeedScope) => Promise<void>;
  feedScope: FeedScope;
  createPostAndAdd: (
    input: socialApi.CreatePostInput,
    /** Current authenticated user – used for post owner so we never display wrong owner from API. */
    currentUser?: { ownerId: string; ownerName?: string; ownerAvatar?: string }
  ) => Promise<SocialPost | null>;
  likePost: (postId: string) => Promise<void>;
  unlikePost: (postId: string) => Promise<void>;
  savePost: (postId: string) => Promise<void>;
  unsavePost: (postId: string) => Promise<void>;
  addComment: (
    postId: string,
    text: string,
    authorDisplay?: string,
    avatarDisplay?: string
  ) => Promise<void>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  updatePost: (
    postId: string,
    data: socialApi.UpdatePostInput,
    taggedPets?: SocialPost['taggedPets']
  ) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

const SocialFeedContext = createContext<SocialFeedContextValue | null>(null);

/**
 * Provides social feed state. Fetches posts from backend on mount; mutations call API and update local state.
 */
export function SocialFeedProvider({ children }: { children: ReactNode }) {
  const [posts, setPostsState] = useState<SocialPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedScope, setFeedScope] = useState<FeedScope>('all');

  const refetch = useCallback(
    async (scope: FeedScope = 'all', isRetry = false) => {
      setIsLoading(true);
      setError(null);
      setFeedScope(scope);
      try {
        const { posts: fetched } = await socialApi.getFeed({
          limit: 50,
          scope:
            scope === 'following'
              ? 'following'
              : scope === 'myPets'
                ? 'myPets'
                : 'all',
        });
        setPostsState(fetched ?? []);
      } catch (err) {
        const msg = getApiErrorMessage(err, 'Failed to load feed');
        const isNetworkError =
          msg?.toLowerCase().includes('network') ||
          msg?.toLowerCase().includes('connection') ||
          msg?.toLowerCase().includes('reach the server');
        if (isNetworkError && !isRetry) {
          try {
            const { posts: fetched } = await socialApi.getFeed({
              limit: 50,
              scope:
                scope === 'following'
                  ? 'following'
                  : scope === 'myPets'
                    ? 'myPets'
                    : 'all',
            });
            setPostsState(fetched);
          } catch (retryErr) {
            setError(getApiErrorMessage(retryErr, 'Failed to load feed'));
            setPostsState([]);
          }
        } else {
          setError(msg);
          setPostsState([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    refetch('all');
  }, [refetch]);

  const setPosts = useCallback((action: React.SetStateAction<SocialPost[]>) => {
    setPostsState((prev) =>
      typeof action === 'function'
        ? (action as (p: SocialPost[]) => SocialPost[])(prev)
        : action
    );
  }, []);

  const createPostAndAdd = useCallback(
    async (
      input: socialApi.CreatePostInput,
      currentUser?: {
        ownerId: string;
        ownerName?: string;
        ownerAvatar?: string;
      }
    ): Promise<SocialPost | null> => {
      try {
        const { post } = await socialApi.createPost(input);
        const ownerId = currentUser?.ownerId ?? input.ownerId ?? post.ownerId;
        const ownerName =
          currentUser?.ownerName ?? input.ownerName ?? post.ownerName;
        const ownerAvatar =
          currentUser?.ownerAvatar ?? input.ownerAvatar ?? post.ownerAvatar;
        const merged: SocialPost = {
          ...post,
          ownerId,
          ownerName: ownerName ?? '',
          ownerAvatar,
          likes: post.likes ?? 0,
          comments: post.comments ?? [],
          isLiked: post.isLiked ?? false,
          isSaved: post.isSaved ?? false,
          taggedPets: post.taggedPets ?? [],
          tags: post.tags ?? [],
          timestamp: post.timestamp ?? post.createdAt ?? new Date().toISOString(),
        };
        setPostsState((prev) => [merged, ...prev]);
        return merged;
      } catch {
        return null;
      }
    },
    []
  );

  const likePost = useCallback(async (postId: string) => {
    try {
      const { likes } = await socialApi.likePost(postId);
      setPostsState((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isLiked: true, likes } : p))
      );
    } catch {
      // optimistic revert handled by caller if needed
    }
  }, []);

  const unlikePost = useCallback(async (postId: string) => {
    try {
      const { likes } = await socialApi.unlikePost(postId);
      setPostsState((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isLiked: false, likes } : p))
      );
    } catch {
      // ignore
    }
  }, []);

  const savePost = useCallback(async (postId: string) => {
    try {
      await socialApi.savePost(postId);
      setPostsState((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isSaved: true } : p))
      );
    } catch {
      // ignore
    }
  }, []);

  const unsavePost = useCallback(async (postId: string) => {
    try {
      await socialApi.unsavePost(postId);
      setPostsState((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isSaved: false } : p))
      );
    } catch {
      // ignore
    }
  }, []);

  const addComment = useCallback(
    async (
      postId: string,
      text: string,
      _authorDisplay?: string,
      avatarDisplay?: string
    ) => {
      try {
        const { comment } = await socialApi.addComment(postId, text);
        if (!comment?.id) {
          toast.error('Could not add comment. Please try again.');
          return;
        }
        // Merge caller-supplied avatar so the commenter's avatar shows immediately
        // even if the API response doesn't include it.
        const enrichedComment = avatarDisplay
          ? { ...comment, avatar: comment.avatar || avatarDisplay }
          : comment;
        setPostsState((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  comments: [
                    ...(Array.isArray(p.comments) ? p.comments : []),
                    enrichedComment,
                  ],
                  likes: p.likes,
                }
              : p
          )
        );
      } catch (err) {
        const msg = getApiErrorMessage(err, 'Failed to add comment');
        toast.error(msg);
      }
    },
    []
  );

  const deleteComment = useCallback(
    async (postId: string, commentId: string) => {
      try {
        await socialApi.deleteComment(postId, commentId);
        setPostsState((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  comments: (p.comments ?? []).filter(
                    (c) => c.id !== commentId
                  ),
                }
              : p
          )
        );
      } catch {
        // ignore
      }
    },
    []
  );

  const updatePost = useCallback(
    async (
      postId: string,
      data: socialApi.UpdatePostInput,
      taggedPets?: SocialPost['taggedPets']
    ) => {
      try {
        const { post } = await socialApi.updatePost(postId, data);
        const merged = taggedPets ? { ...post, taggedPets } : post;
        setPostsState((prev) =>
          prev.map((p) => (p.id === postId ? merged : p))
        );
      } catch {
        // ignore
      }
    },
    []
  );

  const deletePost = useCallback(async (postId: string) => {
    try {
      await socialApi.deletePost(postId);
      setPostsState((prev) => prev.filter((p) => p.id !== postId));
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(
    () => ({
      posts,
      setPosts,
      isLoading,
      error,
      refetch,
      feedScope,
      createPostAndAdd,
      likePost,
      unlikePost,
      savePost,
      unsavePost,
      addComment,
      deleteComment,
      updatePost,
      deletePost,
    }),
    [
      posts,
      setPosts,
      isLoading,
      error,
      refetch,
      feedScope,
      createPostAndAdd,
      likePost,
      unlikePost,
      savePost,
      unsavePost,
      addComment,
      deleteComment,
      updatePost,
      deletePost,
    ]
  );

  return (
    <SocialFeedContext.Provider value={value}>
      {children}
    </SocialFeedContext.Provider>
  );
}

/**
 * Returns feed posts and mutation helpers. Must be used inside SocialFeedProvider.
 */
export function useSocialFeed(): SocialFeedContextValue {
  const context = useContext(SocialFeedContext);
  if (!context) {
    throw new Error('useSocialFeed must be used within SocialFeedProvider');
  }
  return context;
}
